import React, { useEffect, useState } from 'react';
import classNames from 'classnames';
import Button from '@atlaskit/button';
import Spinner from '@atlaskit/spinner';
import ChevronRightIcon from '@atlaskit/icon/glyph/chevron-right';
import ChevronDownIcon from '@atlaskit/icon/glyph/chevron-down';
import { TABLE_LOADER_OPTIONS, TableLoader, DEFAULT_COLUMNS_COUNT } from '@css-xp/ppc-page-loaders';
import './expandable-row.scss';

interface Props {
  testId?: string;
  rowsAsCells: React.ReactNode[][];
  subRows?: React.ReactNode;
  onExpand?: () => void;
  onCollapse?: () => void;
  isSubRowsLoading?: boolean;
  isDefaultExpanded?: boolean;
  className?: string;
  shouldRenderSkeletonLoader?: boolean;
  loaderOptions?: TABLE_LOADER_OPTIONS;
  customLoader?: React.ReactNode;
  isExpandDisabled?: boolean;
  expandableRowColumn?: number;
  isCustomExpanded?: boolean;
}

/* Note: This component adds an extra left padding to its first row cell because
 * expand/collapse button is to be shown. Hence, you need to add the same padding
 * to the first cells (`th`) of its table's `thead` and `subRows`.
 */
// FIX: Need to write this component generically as mentioned in https://hello.atlassian.net/browse/ITFO-8604
export const ExpandableRow = ({
  testId = 'expandable-row',
  rowsAsCells,
  subRows,
  onExpand,
  onCollapse,
  isSubRowsLoading = false,
  isDefaultExpanded = false,
  className,
  shouldRenderSkeletonLoader,
  loaderOptions,
  customLoader,
  isExpandDisabled,
  expandableRowColumn,
  isCustomExpanded,
}: Props) => {
  const [isExpanded, setIsExpanded] = useState<boolean>(isDefaultExpanded);

  const toggleExpanded = () => {
    if (!isExpanded && onExpand) {
      onExpand();
    }
    if (isExpanded && onCollapse) {
      onCollapse();
    }
    setIsExpanded(!isExpanded);
  };

  // This is for resetting the expanded state when `isDefaultExpanded` becomes false
  useEffect(() => {
    if (!isDefaultExpanded) {
      setIsExpanded(isDefaultExpanded);
    }
  }, [isDefaultExpanded]);

  // this effect is to update the expanded state after the initial render
  useEffect(() => {
    if (isCustomExpanded && !isExpanded) {
      setIsExpanded(isCustomExpanded);
    }
  }, [isCustomExpanded]);

  const arrowToShow = isExpanded ? <ChevronDownIcon label="collapse" /> : <ChevronRightIcon label="expand" />;
  const [firstRow, ...restRows] = rowsAsCells;

  if (!firstRow?.length) {
    return null;
  }

  // Add expand/collapse button to the first cell of first row of `rowsAsCells` prop
  const newRowCells = [...firstRow];
  if (newRowCells && newRowCells?.length > 0 && (subRows || onExpand)) {
    // If expandableRowColumn is not provided, then add expand/collapse button to the first cell
    const expandableCellIndex = expandableRowColumn ?? 0;
    newRowCells[expandableCellIndex] = (
      <td data-testId={`expandable-button-column-${expandableCellIndex}`} className="expandable-row-cell">
        <Button
          className="expandable-row-button"
          iconBefore={arrowToShow}
          appearance="subtle"
          onClick={toggleExpanded}
          testId="expandable-row-button"
          isDisabled={isExpandDisabled}
        />
        <table className="first-cell-table">
          <tbody>
            <tr>{firstRow[expandableCellIndex]}</tr>
          </tbody>
        </table>
      </td>
    );
  }

  const renderLoader = () => {
    if (customLoader) return customLoader;

    if (shouldRenderSkeletonLoader) {
      return (
        <tr className={classNames('expandable-loader-v2')}>
          <td colSpan={loaderOptions?.columnsCount || DEFAULT_COLUMNS_COUNT}>
            <TableLoader
              testId="expandable-row-loader"
              columnsCount={loaderOptions?.columnsCount}
              columnsWidth={loaderOptions?.columnsWidth}
              filtersCount={loaderOptions?.filtersCount}
              hideActions={loaderOptions?.hideActions}
              hideFilters={loaderOptions?.hideFilters}
              hideSearchBar={loaderOptions?.hideSearchBar}
              hideHeader={loaderOptions?.hideHeader}
              shouldRightAlignLastCol={loaderOptions?.shouldRightAlignLastCol}
              rowsCount={loaderOptions?.rowsCount}
            />
          </td>
        </tr>
      );
    }

    return (
      <div className={classNames('no-data-view pad-vertical expandable-loader')}>
        <Spinner testId={`expand-loading`} size={'large'} />
      </div>
    );
  };

  const remainingRows =
    restRows?.length > 0 &&
    restRows.map((rowCells, index) => (
      <tr className="expandable-row-rest" key={index}>
        {rowCells}
      </tr>
    ));

  return (
    <>
      <tr data-testid={testId} className={`expandable-row ${className}`}>
        {newRowCells}
      </tr>
      {remainingRows}
      {isExpanded && isSubRowsLoading && renderLoader()}
      {isExpanded && !isSubRowsLoading && subRows && subRows}
    </>
  );
};
